// Scripted stand-ins for the four speech ports. They answer on timers so a
// test with fake timers, or a page in "?fake=1" mode, can run a whole spoken
// turn with no microphone, no gateway and no speaker.
import type { VoiceCapturePort, VoiceCaptureSession, VoiceCaptureResult } from '../../ports/VoiceCapturePort';
import type { VoiceCaptureError } from '../../ports/VoiceCapturePort';
import type { SpeechToTextPort, SpeechToTextRequest, SpeechToTextResult } from '../../ports/SpeechToTextPort';
import type { SpeechToTextError } from '../../ports/SpeechToTextPort';
import type { TextToSpeechPort, TextToSpeechRequest, TextToSpeechResult } from '../../ports/TextToSpeechPort';
import type { BrowserAudioPlaybackPort, BrowserAudioPlaybackSession } from '../../ports/BrowserAudioPlaybackPort';
import type { SpeechPorts } from '../../ports/SpeechPorts';

export type { SpeechPorts } from '../../ports/SpeechPorts';

export const FAKE_SPEECH_DELAYS = { transcribe: 400, synthesize: 200, play: 600 } as const;
export const FAKE_TRANSCRIPT = 'Who are you?';

function after<T>(ms: number, value: () => T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value()), ms));
}

export class FakeCaptureSession implements VoiceCaptureSession {
  mimeType = 'audio/webm';
  stopped = false;
  cancelled = false;
  constructor(private readonly durationMs: number) {}
  async stop(): Promise<VoiceCaptureResult> {
    this.stopped = true;
    return { blob: new Blob(['fake audio'], { type: this.mimeType }), mimeType: this.mimeType, durationMs: this.durationMs };
  }
  async cancel(): Promise<void> { this.cancelled = true; }
}

export class FakeCapture implements VoiceCapturePort {
  starts = 0;
  durationMs = 1500;
  failWith: VoiceCaptureError | null = null;
  lastSession: FakeCaptureSession | null = null;
  async start(): Promise<VoiceCaptureSession> {
    this.starts += 1;
    if (this.failWith) throw this.failWith;
    this.lastSession = new FakeCaptureSession(this.durationMs);
    return this.lastSession;
  }
}

export class FakeSpeechToText implements SpeechToTextPort {
  requests: SpeechToTextRequest[] = [];
  failWith: SpeechToTextError | null = null;
  text = FAKE_TRANSCRIPT;
  transcribe(request: SpeechToTextRequest): Promise<SpeechToTextResult> {
    this.requests.push(request);
    if (this.failWith) return Promise.reject(this.failWith);
    return after(FAKE_SPEECH_DELAYS.transcribe, () => ({ text: this.text, provider: 'fake' }));
  }
}

export class FakeTextToSpeech implements TextToSpeechPort {
  requests: TextToSpeechRequest[] = [];
  failWith: Error | null = null;
  synthesize(request: TextToSpeechRequest): Promise<TextToSpeechResult> {
    this.requests.push(request);
    if (this.failWith) return Promise.reject(this.failWith);
    return after(FAKE_SPEECH_DELAYS.synthesize, () => ({
      audio: new Blob(['fake mp3'], { type: 'audio/mpeg' }), contentType: 'audio/mpeg', provider: 'fake',
    }));
  }
}

export class FakePlayback implements BrowserAudioPlaybackPort {
  plays: TextToSpeechResult[] = [];
  stops = 0;
  failWith: Error | null = null;
  private ended: (() => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  async play(result: TextToSpeechResult): Promise<BrowserAudioPlaybackSession> {
    if (this.failWith) throw this.failWith;
    this.plays.push(result);
    this.timer = setTimeout(() => this.endCurrent(), FAKE_SPEECH_DELAYS.play);
    return {
      stop: () => { this.stops += 1; this.endCurrent(); },
      onEnded: (cb) => { this.ended = cb; },
    };
  }
  /** Fires the current session's ended callback once. */
  endCurrent(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    const cb = this.ended;
    this.ended = null;
    cb?.();
  }
}

export function createScriptedSpeechPorts(): SpeechPorts {
  return {
    capture: new FakeCapture(),
    speechToText: new FakeSpeechToText(),
    textToSpeech: new FakeTextToSpeech(),
    playback: new FakePlayback(),
  };
}
